import { useEffect, useRef, useState } from "react";

const DURACAO_DESCIDA = 220;

/**
 * Fechar um bottom sheet tocando na alça (o traço no topo): o painel desce e o fundo some antes de
 * desmontar, como se tivesse sido arrastado para baixo. Só a alça anima — o X, o Escape, o clique no
 * fundo e o voltar do Android continuam fechando na hora.
 *
 * Existe porque a alça está em três esqueletos de sheet diferentes (OverlaySheet, PainelProfissional,
 * OverlayCalendario), e a animação feita só no primeiro deixou o traço morto nos outros dois.
 *
 * Timer, e não transitionend: o evento não vem quando a transição é cortada (aba em segundo plano) e
 * o sheet ficaria preso aberto. Com prefers-reduced-motion fecha direto.
 *
 * `aberto` é para quem continua montado entre aberturas (OverlaySheet, OverlayCalendario): reabrir
 * depois de uma descida devolve o painel ao lugar. Quem é montado e desmontado pelo pai omite.
 *
 * Os estilos vão inline porque a alça só existe abaixo de md: o transform nunca chega a brigar com
 * os md:-translate que centram o cartão no desktop. Quem usa põe `transition-opacity ease-in` no
 * fundo e `transition-transform ease-in` no painel — ease-in porque ele sai acelerando, como solto.
 */
export default function useDescidaDaAlca(onFechar, aberto = true) {
	const [descendo, setDescendo] = useState(false);
	const timerRef = useRef(null);
	const aoFecharRef = useRef(onFechar);
	aoFecharRef.current = onFechar;

	useEffect(() => {
		if (aberto) setDescendo(false);
		return () => clearTimeout(timerRef.current);
	}, [aberto]);

	const fecharPelaAlca = () => {
		if (descendo) return;
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			aoFecharRef.current();
			return;
		}
		setDescendo(true);
		timerRef.current = setTimeout(() => aoFecharRef.current(), DURACAO_DESCIDA);
	};

	const duracao = { transitionDuration: `${DURACAO_DESCIDA}ms` };

	return {
		fecharPelaAlca,
		estiloFundo: { ...duracao, opacity: descendo ? 0 : 1 },
		estiloPainel: { ...duracao, ...(descendo && { transform: "translateY(100%)" }) },
	};
}
